import { Directive, Input } from '@angular/core';
import { FeedbackService } from '../../core/services/feedback.service';
import {
  applyScopeReplace,
  canRevertScopeReplace,
  FindReplaceTarget,
  revertScopeReplace,
  ScopeReplaceSummary,
  ScopeUndoEntry,
  scopeHits,
  totalMatches
} from '../util/find-replace-scope';

/**
 * Groups a set of free-text fields so find & replace can span all of them.
 *
 *   <div [appFindReplaceScope]="findReplaceTargets" findReplaceScopeName="this AI request">
 *
 * Every `textarea[appFindReplace]` underneath picks this up through the element
 * injector and grows an extra "All fields" button. The scope owns the confirmation
 * step and the cross-field undo; the textarea directives stay unaware of each other.
 *
 * The bound targets are FormControls, not the rendered textareas, so fields sitting
 * in a collapsed — or entirely un-rendered — section are still covered.
 */
@Directive({
  selector: '[appFindReplaceScope]',
  exportAs: 'appFindReplaceScope'
})
export class FindReplaceScopeDirective {
  /** The fields this scope covers. Re-read on each use, so a getter is fine. */
  @Input('appFindReplaceScope') targets: FindReplaceTarget[] | null = null;

  /** Named in the confirmation dialog: "…across every field in <name>". */
  @Input() findReplaceScopeName = 'this form';

  private undoEntries: ScopeUndoEntry[] = [];

  constructor(private feedback: FeedbackService) {}

  get list(): FindReplaceTarget[] {
    return this.targets ?? [];
  }

  /** Total matches for `term` across the whole scope — drives the button's count. */
  countMatches(term: string): number {
    return totalMatches(scopeHits(this.list, term));
  }

  /**
   * Confirms, then rewrites every match across the scope.
   *
   * @returns the summary, or null if there was nothing to do or the user backed out.
   */
  async replaceEverywhere(term: string, replacement: string): Promise<ScopeReplaceSummary | null> {
    const hits = scopeHits(this.list, term);
    if (!hits.length) {
      return null;
    }

    const occurrences = totalMatches(hits);
    const confirmed = await this.feedback.showConfirmation(
      'Replace across all fields?',
      `Replace ${this.plural(occurrences, 'occurrence')} of "${term}" with ` +
        `"${replacement}" across ${this.plural(hits.length, 'field')} in ${this.findReplaceScopeName}. ` +
        `This includes sections you may not have open:`,
      // The dialog's list renders `items.patient` for plain-string rows.
      hits.map(hit => ({ patient: `${hit.target.label} — ${this.plural(hit.count, 'match', 'matches')}` })),
      'Cancel',
      'Replace all'
    );

    if (!confirmed) {
      return null;
    }

    const { summary, undo } = applyScopeReplace(this.list, term, replacement);
    this.undoEntries = undo;

    if (summary.occurrences) {
      this.feedback.showToast(
        `Replaced ${this.plural(summary.occurrences, 'occurrence')} across ` +
          `${this.plural(summary.fields, 'field')}.`
      );
    }
    return summary;
  }

  /** True while every field this scope rewrote still reads as it left it. */
  canUndo(): boolean {
    return canRevertScopeReplace(this.undoEntries);
  }

  undo(): void {
    if (!this.canUndo()) {
      return;
    }
    const restored = revertScopeReplace(this.undoEntries);
    this.undoEntries = [];
    if (restored) {
      this.feedback.showToast(`Reverted the replace across ${this.plural(restored, 'field')}.`);
    }
  }

  private plural(count: number, singular: string, plural = `${singular}s`): string {
    return `${count} ${count === 1 ? singular : plural}`;
  }
}
